import { LevelSubScreen } from '@v2/pages/common/enums';
import { isWeb, navigateTo, navigationRef } from '@helpers';
import { clearHeaderConfig, clearPerspectiveTabHistory, clearTabHistory } from '@reducers/details';
import { useNavigationCheck } from '@v2/pages/machines/hooks/useNavigationCheck';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { WebDetailsScreenName } from '@v2/pages/common/constants/base.constants';
import { useHandleBottomSheetActions } from './useHandleBottomSheetActions';
import { trackOpenedEvent } from '@services/tracking/service/trackOpenedEvent';
import { SCREEN_TO_TRACKING_EVENT } from '@services/tracking/helpers';
import { inferSourceScreen } from '@services/tracking/helpers';
import type { SourceScreen } from '@services/tracking';

export const useHandleMainDetailsScreenNavigation = () => {
  const dispatch = useAppDispatch();
  const { safeIsMachinesScreenCheck, safeIsDetailsScreen } = useNavigationCheck();
  const { openScreenInsideBottomSheet } = useHandleBottomSheetActions();

  const navigateToMainDetailsScreen = ({
    mainScreen,
    subScreen,
    levelSubScreen = LevelSubScreen.GENERAL,
    params,
    sourceScreen,
  }: {
    mainScreen: string;
    subScreen: string;
    params: Record<string, any>;
    levelSubScreen?: string | null;
    sourceScreen?: SourceScreen;
  }) => {

    // Track item detail opened event — auto-detect sourceScreen from current route if not provided
    const trackingEvent = SCREEN_TO_TRACKING_EVENT[mainScreen];
    if (trackingEvent) {
      const currentRouteName = navigationRef?.current?.getCurrentRoute()?.name;
      trackOpenedEvent(trackingEvent, {
        ...(params.batchId ? { productionBatchId: String(params.batchId) } : {}),
        ...(params.ncFileId ? { ncFileId: String(params.ncFileId) } : {}),
        ...(params.productionOperationId ? { productionOperationId: String(params.productionOperationId) } : {}),
        ...(params.productionOrderId ? { productionOrderId: String(params.productionOrderId) } : {}),
        ...(params.partId ? { partId: String(params.partId) } : {}),
        ...(params.toolId ? { toolId: String(params.toolId) } : {}),
        sourceScreen: sourceScreen || inferSourceScreen(currentRouteName),
      });
    }

    //clear headerConfig in any case because we don't want to show previous names
    dispatch(clearHeaderConfig());

    if (!safeIsDetailsScreen) {
      openScreenInsideBottomSheet({ bottomSheetId: WebDetailsScreenName });

      // clear tab history for details screens
      dispatch(clearPerspectiveTabHistory());
      dispatch(clearTabHistory());
    }

    if (isWeb) {
      if (safeIsDetailsScreen) {
        // We're already inside WebDetails, navigate within it using navigationRef
        navigationRef.current?.navigate(mainScreen, {
          screen: subScreen,
          ...(!levelSubScreen
            ? { params }
            : {
                params: {
                  screen: subScreen + levelSubScreen,
                  params,
                },
              }),
        });
      } else {
        // First time entering details, navigate to WebDetails wrapper
        navigateTo(WebDetailsScreenName, {
          screen: mainScreen,
          params: {
            screen: subScreen,
            ...(!levelSubScreen
              ? { params }
              : {
                  params: {
                    screen: subScreen + levelSubScreen,
                    params,
                  },
                }),
          },
        });
      }
    } else if (safeIsMachinesScreenCheck) {
      // On mobile from machines screen
      navigateTo(mainScreen, {
        screen: subScreen,
        ...(!levelSubScreen
          ? { params }
          : {
              params: {
                screen: subScreen + levelSubScreen,
                params,
              },
            }),
      });
    } else {
      navigateTo('SearchScreen', {
        screen: mainScreen,
        params: {
          screen: subScreen,
          ...(!levelSubScreen
            ? { params }
            : {
                params: {
                  screen: subScreen + levelSubScreen,
                  params,
                },
              }),
        },
      });
    }
  };

  return { navigateToMainDetailsScreen };
};
